/**
 * Очередь отложенных действий.
 *
 * Скаут работает в паркинге и в подвале. «Взять задачу» и «завершить» обязаны
 * приниматься без сети и досылаться потом, иначе приложение бесполезно ровно
 * там, где стоят сломанные байки.
 *
 * ПОЧЕМУ IndexedDB, А НЕ Cache Storage.
 * В Aida-PWA есть жёсткое правило «/v1/* не кэшировать никогда» (src/sw.ts).
 * Оно защищает от реальной дыры: Cache Storage ключуется по URL запроса и НЕ
 * разделяется по заголовку Authorization — ответ одного пользователя может
 * быть отдан другому. Здесь офлайн нужен, поэтому правило не отменяется, а
 * соблюдается другим способом: данные API не попадают в Cache Storage вообще,
 * а лежат в IndexedDB под ключом, куда входит id пользователя, и стираются
 * при выходе и при смене пользователя. См. clearUserScopedStorage().
 * Не «чинить» это переносом в Cache Storage.
 *
 * ИДЕМПОТЕНТНОСТЬ.
 * id записи — это же и X-Idempotency-Key. Платформа хранит ключи в Redis
 * (pkg/idem) и пробрасывает заголовок от BFF в домен, поэтому повторная
 * доставка одной и той же записи не создаст второго действия. Ключ обязан
 * пережить перезапуск приложения — поэтому генерируется при постановке
 * в очередь, а не при отправке.
 */
import { del, get, set } from 'idb-keyval'
import { ApiError, NetworkError, request } from '../http'
import { statusActionPath } from '../vehicle-status'
import { LOCK_WAIT_TIMEOUT_MS } from '../collector'
import { z } from 'zod'

/**
 * Ключ очереди СВОЙ У КАЖДОГО ПОЛЬЗОВАТЕЛЯ.
 *
 * ⚠ Раньше был один на весь браузер — при том что кэш чтения уже ключевался
 * по пользователю (`lib/offline/cache.ts`), и шапка этого файла обещала то же
 * самое про очередь. Обещание не выполнялось.
 *
 * Цена расхождения видна на сменном телефоне парка: `clearOutbox()` зовётся
 * только из явного «Выйти», а вход второго скаута поверх чужой сессии её не
 * чистит. Неотправленные действия первого уходили бы на сервер **под токеном
 * второго** — `request()` берёт заголовок из текущих токенов, а не из момента
 * постановки в очередь.
 */
const KEY_PREFIX = 'aida.scout.outbox'

/** Ключ до появления разделения по пользователю. См. migrateLegacyKey. */
const LEGACY_KEY = KEY_PREFIX

let currentUser = ''

/**
 * Кому принадлежит очередь. Ставится из `providers.tsx` при появлении и смене
 * сессии.
 *
 * Пустая строка (никто не вошёл) означает «работаем со старым общим ключом»:
 * до входа очередь всё равно некому досылать, а терять записи, поставленные
 * до обновления, нельзя.
 */
export async function setOutboxUser(userId: string): Promise<void> {
  if (currentUser === userId) return
  currentUser = userId
  await migrateLegacyKey()
  // Подписчики держат список ПРЕДЫДУЩЕГО пользователя — счётчик «N действий
  // ждут связи» показывал бы чужие. Перечитываем сразу.
  await notify()
}

function outboxKey(): string {
  return currentUser ? `${KEY_PREFIX}.${currentUser}` : LEGACY_KEY
}

/**
 * Разовый перенос записей со старого общего ключа на текущего пользователя.
 *
 * ⚠ ЭТО ДОПУЩЕНИЕ, И ОНО НАЗВАНО ВСЛУХ. Кто именно поставил эти записи, мы не
 * знаем — поля пользователя в них нет. Перенос ровно один раз воспроизводит ту
 * самую утечку, которую разделение ключей закрывает.
 *
 * Выбрано так, потому что альтернатива хуже: обновление прилетает скауту в
 * середине смены, и в очереди в этот момент вполне лежит неотправленное
 * «завершить». Молча выбросить сделанную в поле работу — потеря, а перенос —
 * риск, реализуемый только если телефоном за это же обновление успели
 * попользоваться двое без выхода из системы.
 */
async function migrateLegacyKey(): Promise<void> {
  if (!currentUser) return
  try {
    const legacy = await get<StoredEntry[]>(LEGACY_KEY)
    if (!legacy || legacy.length === 0) return
    const mine = (await get<StoredEntry[]>(outboxKey())) ?? []
    await set(outboxKey(), [...mine, ...legacy])
    await del(LEGACY_KEY)
  } catch (e) {
    console.error('[outbox] не удалось перенести очередь прошлой версии', e)
  }
}

/** Действие, которое можно отложить. Список закрыт намеренно. */
export type OutboxKind =
  | 'claim'
  | 'complete'
  | 'vehicle_status'
  | 'create_pickup'
  | 'create_rebalance'
  | 'vehicle_report'
  | 'label_attach'
  | 'label_detach'
  | 'pool_pick'
  | 'pool_deliver'
  | 'pool_drop'
  | 'collector_cancel'

export type OutboxEntry = {
  /** UUID. Он же X-Idempotency-Key. Генерируется один раз, при постановке. */
  id: string
  kind: OutboxKind
  /** Над чем действие: id задачи для claim/complete, id байка для vehicle_status, create_pickup и pool_*. */
  targetId: string
  /** Тело запроса. Для claim — пустое. */
  payload: Record<string, unknown>
  /** RFC3339 UTC, когда скаут нажал кнопку (а не когда мы дослали). */
  queuedAt: string
  attempts: number
  /** Текст последней ошибки — показываем скауту, если досылка не удалась. */
  lastError?: string
  /**
   * HTTP-код отказа.
   *
   * Хранится рядом с `lastReason`, потому что отвечает на другой вопрос:
   * reason говорит, ЧТО решил сервер, а 404 — что сервер про этот маршрут
   * вообще не знает, и reason у него в этом случае пустой.
   */
  lastStatus?: number
  /**
   * `reason` сервера как есть.
   *
   * Хранится ОТДЕЛЬНО от текста, потому что решение «это сбой или штатный
   * исход» принимается по нему, а не по русской строке: reason — контракт,
   * текст — нет. См. rejectedTone.
   */
  lastReason?: string
}

/**
 * Запись в том виде, в каком она могла быть сохранена ПРОШЛОЙ версией
 * приложения: до появления действий над байком целью всегда была задача, и
 * поле называлось `taskId`.
 *
 * ⚠ Обратная совместимость здесь обязательна, а не вежлива. Обновление
 * прилетает скауту в середине смены, и в очереди в этот момент вполне может
 * лежать неотправленное «завершить». Читать такую запись как запись без цели
 * значит потерять действие — ровно то, ради предотвращения чего очередь и
 * существует.
 */
type StoredEntry = Omit<OutboxEntry, 'targetId'> & {
  targetId?: string
  taskId?: string
}

type Subscriber = (entries: OutboxEntry[]) => void
const subscribers = new Set<Subscriber>()

/** Подписка на изменения очереди — бейдж «N не отправлено» в интерфейсе. */
export function subscribeOutbox(fn: Subscriber): () => void {
  subscribers.add(fn)
  // readOutbox не бросает, поэтому подписка безопасна и при мёртвом хранилище.
  void readOutbox().then(fn)
  return () => {
    subscribers.delete(fn)
  }
}

async function notify(): Promise<void> {
  const entries = await readOutbox()
  subscribers.forEach((fn) => {
    fn(entries)
  })
}

/**
 * Хранилище недоступно: приватный режим, запрет на данные сайта, добитая квота.
 *
 * Отдельный тип, потому что для чтения и для записи это разные по тяжести
 * события. Не прочитали — покажем пустой список, скаут переживёт. Не записали —
 * действие НЕ поставлено в очередь и не будет доставлено никогда; молчать об
 * этом нельзя, иначе «Завершить» выглядит нажатым, а задача остаётся открытой.
 */
export class OutboxStorageError extends Error {
  constructor(cause?: unknown) {
    super('Не удалось сохранить действие на устройстве')
    this.name = 'OutboxStorageError'
    this.cause = cause
  }
}

/**
 * Чтение очереди. Никогда не бросает: недоступное хранилище даёт пустой
 * список. Иначе один сбой IndexedDB роняет весь экран.
 */
export async function readOutbox(): Promise<OutboxEntry[]> {
  try {
    const raw = await get<StoredEntry[]>(outboxKey())
    return (raw ?? []).map(migrate)
  } catch (e) {
    console.error('[outbox] хранилище недоступно для чтения', e)
    return []
  }
}

/** Дочитывает записи прошлой версии. См. StoredEntry. */
function migrate(e: StoredEntry): OutboxEntry {
  const { taskId, ...rest } = e
  return { ...rest, targetId: e.targetId ?? taskId ?? '' }
}

/** Запись. БРОСАЕТ при сбое — вызывающий обязан сообщить скауту. */
async function writeOutbox(entries: OutboxEntry[]): Promise<void> {
  try {
    if (entries.length === 0) {
      await del(outboxKey())
      return
    }
    await set(outboxKey(), entries)
  } catch (e) {
    throw new OutboxStorageError(e)
  }
}

/**
 * Идентификатор записи, он же ключ идемпотентности.
 *
 * ⚠ `crypto.randomUUID` доступен ТОЛЬКО в защищённом контексте. В Capacitor и
 * на https он есть, но приложение открывают и по `http://<адрес в сети>:5174`
 * при отладке — а там его нет вовсе, и без запасного пути обрывается весь путь
 * «Взять»: очередь не принимает действие, кнопка выглядит нажатой.
 *
 * Запасной вариант строится на `crypto.getRandomValues` (он есть и в
 * незащищённом контексте) и даёт настоящий UUIDv4. Последнее средство —
 * Math.random: хуже по качеству, но ключ обязан быть хоть каким-то, иначе
 * действие теряется. Уникальности в пределах одного скаута достаточно:
 * сервер сверяет ключ в паре с пользователем.
 */
export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (typeof c?.randomUUID === 'function') return c.randomUUID()

  const bytes = new Uint8Array(16)
  if (typeof c?.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  // Разметка версии 4 и варианта RFC 4122.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80

  const hex: string[] = []
  for (const b of bytes) hex.push(b.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

/** Ставит действие в очередь. Возвращает запись — у неё уже есть id/ключ. */
export async function enqueue(
  kind: OutboxKind,
  targetId: string,
  payload: Record<string, unknown> = {},
): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: newId(),
    kind,
    targetId,
    payload,
    queuedAt: new Date().toISOString(),
    attempts: 0,
  }
  const entries = await readOutbox()
  entries.push(entry)
  await writeOutbox(entries)
  await notify()
  return entry
}

const emptyReply = z.unknown()

/** Всё уходит POST'ом, кроме снятия лейбла: у него DELETE и тела нет. */
function methodFor(entry: OutboxEntry): 'POST' | 'DELETE' {
  return entry.kind === 'label_detach' ? 'DELETE' : 'POST'
}

/**
 * Куда уходит запись.
 *
 * У смены статуса маршрут зависит от `payload.action`: сколько статусов —
 * столько ручек (`faulty`, `lost`, `in-transit`, `at-base`), общей ручки
 * «поставь статус» на бэкенде нет.
 *
 * Подчёркивание меняется на дефис ОБЩИМ правилом, а не перечислением.
 * Статус называется `in_transit`, а ручка — `/in-transit`; когда рядом
 * появился `at_base`, список из одного исключения молча дал бы `/at_base`
 * и 404 — уже после того, как действие приняли у скаута и положили в очередь.
 */
function pathFor(entry: OutboxEntry): string {
  switch (entry.kind) {
    case 'claim':
      return `/v1/scout/tasks/${entry.targetId}/claim`
    case 'complete':
      return `/v1/scout/tasks/${entry.targetId}/complete`
    // Единственный маршрут без цели в пути: задачи ещё не существует, и
    // байк едет в теле. targetId здесь — id байка, как у vehicle_status.
    case 'create_pickup':
    case 'create_rebalance':
      return '/v1/scout/tasks'
    case 'vehicle_report':
      return `/v1/vehicles/${entry.targetId}/report`
    // Привязка и отвязка — один путь, разные методы (`methodFor`).
    case 'label_attach':
    case 'label_detach':
      return `/v1/vehicles/${entry.targetId}/labels/${encodeURIComponent(String(entry.payload.labelId ?? ''))}`
    /*
     * ⚠ ЗДЕСЬ БЫЛ БАГ, И ОН СТОИЛ «ВЕРНУТЬ В ЛИНИЮ» ЦЕЛИКОМ.
     *
     * Стояло `String(action).replaceAll('_', '-')` — самодельное преобразование
     * рядом с настоящей таблицей путей, которая жила в `api/vehicles.ts`. Для
     * пяти действий из шести замена случайно совпадала с ручкой, а для
     * `available` давала `/available` вместо `/recovered`: маршрута с таким
     * именем нет, сервер отвечал 404, и скаут видел «ждём выката» вместо
     * смены статуса.
     *
     * Тест на карту путей при этом был зелёным — он проверял функцию, которую
     * реальная отправка не вызывала. Теперь вызывает: источник один.
     */
    case 'vehicle_status':
      return `/v1/vehicles/${entry.targetId}/${statusActionPath(String(entry.payload.action ?? ''))}`
    // Пул водителя: байк в пути, действие — последним сегментом.
    case 'pool_pick':
      return `/v1/scout/pool/${entry.targetId}/pick`
    case 'pool_deliver':
      return `/v1/scout/pool/${entry.targetId}/deliver`
    case 'pool_drop':
      return `/v1/scout/pool/${entry.targetId}/drop`
    case 'collector_cancel':
      return `/v1/collector/tasks/${entry.targetId}/cancel`
  }
}

/**
 * Тело запроса.
 *
 * `action` из тела вычищается: он выбирает маршрут, а сервер такого поля не
 * знает. Отправить его значило бы полагаться на то, что незнакомые поля
 * молча игнорируются, — а это ровно та привычка, из-за которой параметр
 * `status` однажды уже уехал в никуда незамеченным.
 */
function bodyFor(entry: OutboxEntry): Record<string, unknown> {
  // Оба id лейбла — в пути; `labelId` из payload в теле не нужен.
  if (entry.kind === 'label_attach') return {}
  /*
   * ⚠ Здесь `id` НЕ подставляется, в отличие от всех остальных видов.
   * У `CreateScoutTaskRequest` (public.proto) поля `id` нет вовсе — есть
   * `type`, `vehicle_id`, `note` и ещё три, которые скауту не разрешены.
   * Отправить лишнее поле значило бы положиться на то, что незнакомое молча
   * игнорируется, — ровно та привычка, из-за которой параметр `status`
   * однажды уже уехал в никуда незамеченным.
   */
  if (entry.kind === 'create_pickup') {
    return {
      type: 'pickup',
      vehicleId: entry.targetId,
      note: String(entry.payload.note ?? ''),
    }
  }
  /*
   * ⚠ `loaded: true` — обязательное поле, а не подробность. Оно означает
   * «байк уже у меня в руках»: сервер тем же запросом переводит его в
   * `in_transit`, и на время перестановки байк перестаёт быть бронируемым,
   * а его движение перестаёт выглядеть кражей. Без флага это была бы просьба
   * «кто-нибудь переставьте», а байк остался бы доступен клиентам под руками
   * у того, кто его уже везёт.
   */
  if (entry.kind === 'create_rebalance') {
    return {
      type: 'rebalance',
      vehicleId: entry.targetId,
      note: String(entry.payload.note ?? ''),
      loaded: true,
    }
  }
  /*
   * Пул: тело — ровно то, что положили при нажатии. `id` не подставляется:
   * у этих сообщений его нет, байк едет в пути. Координаты — водителя В
   * МОМЕНТ НАЖАТИЯ, а не досылки: иначе «На линию» поставил бы байк туда,
   * где у водителя появилась сеть.
   */
  if (entry.kind === 'pool_pick' || entry.kind === 'pool_deliver' || entry.kind === 'pool_drop') {
    return { ...entry.payload }
  }
  // Отмена сборщика: байк в пути, в теле — причина и комментарий.
  if (entry.kind === 'collector_cancel') return { ...entry.payload }
  if (entry.kind === 'vehicle_status') {
    const { action: _action, ...rest } = entry.payload
    return { id: entry.targetId, ...rest }
  }
  /*
   * ⚠ У ReportProblem ключ байка называется `vehicleId`, а НЕ `id`.
   * Ветка по умолчанию ниже подставила бы `id`, `vehicle_id` уехал бы пустым,
   * и `fleet` вернул бы 400 VEHICLE_ID_REQUIRED — уже после того, как
   * замечание приняли у скаута и положили в очередь.
   */
  if (entry.kind === 'vehicle_report') {
    return {
      vehicleId: entry.targetId,
      category: String(entry.payload.category ?? 'other'),
      note: String(entry.payload.note ?? ''),
    }
  }
  return { id: entry.targetId, ...entry.payload }
}

/**
 * Результат одной попытки досылки.
 *
 * У отказа есть текст: причину сервера надо донести до скаута, а до момента
 * показа она иначе не доживает — запись из очереди при отказе удаляется.
 */
type SendOutcome =
  | { outcome: 'sent' | 'retry' }
  | { outcome: 'rejected'; error: string; reason?: string; status: number }

async function send(entry: OutboxEntry): Promise<SendOutcome> {
  /*
   * Маршрут считается ДО try, и это важно.
   *
   * `pathFor` бросает на незнакомом действии — то есть на программной ошибке,
   * а не на отказе сервера. Попади этот бросок в catch ниже, он подошёл бы под
   * ветку «неизвестная ошибка → retry», и запись блокировала бы всю очередь
   * навсегда, молча: повторять её бессмысленно, сколько ни жди.
   *
   * Поэтому такая запись ОТКЛОНЯЕТСЯ: она уходит из очереди, скаут видит
   * причину, остальные действия проходят дальше.
   */
  let path: string
  try {
    path = pathFor(entry)
  } catch (e) {
    console.error('[outbox] не удалось построить маршрут', entry, e)
    return {
      outcome: 'rejected',
      error: 'приложение не знает, куда отправить это действие',
      status: 0,
    }
  }

  try {
    await request(path, emptyReply, {
      method: methodFor(entry),
      body: entry.kind === 'label_detach' ? undefined : bodyFor(entry),
      idempotencyKey: entry.id,
      // Отмена «в машине» закрывает замок на сервере — до ~2 мин. Потолок —
      // чтобы зависший ответ не держал очередь вечно; не дождались — повтор
      // с тем же ключом, как при обрыве связи.
      ...(entry.kind === 'collector_cancel' ? { timeoutMs: LOCK_WAIT_TIMEOUT_MS } : {}),
    })
    return { outcome: 'sent' }
  } catch (e) {
    // Связи не было — запись остаётся, повторим позже.
    if (e instanceof NetworkError) return { outcome: 'retry' }
    if (e instanceof ApiError) {
      if (!e.isClient) {
        /*
         * Отмена сборщика: отказ замка — окончательный. Повтор слал бы замку
         * новую команду на каждой досылке и держал очередь, а «Вернул» — за
         * ней (финальное ревью волны 3, I-1).
         */
        if (entry.kind === 'collector_cancel' && LOCK_REFUSALS.has(e.reason)) {
          return { outcome: 'rejected', error: rejectionReason(e), reason: e.reason, status: e.status }
        }
        // 5xx — сервер временно не в себе, повторяем.
        return { outcome: 'retry' }
      }
      // 401 обрабатывается внутри request(): если дошло сюда — сессия кончилась,
      // повторять бессмысленно до нового входа.
      if (e.status === 401) return { outcome: 'retry' }
      // Остальные 4xx — отказ по существу: задачу уже взял другой (409),
      // переход невозможен (409 STATUS_TRANSITION_INVALID), не тот исполнитель
      // (403). Повтор ничего не изменит, скауту надо показать причину.
      return { outcome: 'rejected', error: rejectionReason(e), reason: e.reason, status: e.status }
    }
    return { outcome: 'retry' }
  }
}

/** 503 замка, которые повтор не вылечит. */
const LOCK_REFUSALS = new Set(['LOCK_FAILED', 'DEVICE_OFFLINE', 'BIKE_LOCK_UNAVAILABLE'])

/**
 * Причина отказа человеческим языком.
 *
 * Разбор по `reason`, а не по тексту сообщения: правило бэкенда
 * (`CLAUDE.md`) — ловить `ApiError` и смотреть на `reason`. Текст сервера
 * английский и меняется; reason — контракт.
 */
function rejectionReason(e: ApiError): string {
  switch (e.reason) {
    case 'STATUS_TRANSITION_INVALID':
      return 'из текущего статуса так нельзя — возможно, байк уже перевели'
    case 'SCOUT_TASK_REQUIRED':
      return 'этот байк не в вашей задаче'
    case 'STAFF_REQUIRED':
      return 'недостаточно прав'
    case 'SCOUT_TASK_NOT_ASSIGNEE':
      return 'задача взята не вами'
    case 'VEHICLE_HAS_ACTIVE_TASK':
      return 'на этот байк задача уже есть'
    // Текст не называет разрешённый тип: он зависит от роли, а роль клиент
    // сервером не проверяет. «Забрать» доступно скауту, «Переставить» —
    // и скауту, и партнёру; остальное не заводится из поля вовсе.
    case 'SCOUT_TASK_TYPE_FORBIDDEN':
      return 'такую задачу вам создавать нельзя'
    // Одно активное задание за раз. Не сбой и не гонка: столько байков в
    // руках всё равно не унести.
    case 'PARTNER_TASK_ACTIVE':
      return 'сначала завершите текущую перестановку'
    // Кнопка гасится по статусу из кэша, а он мог протухнуть, пока шли к
    // байку. Перестановка не должна возвращать сломанный байк на линию.
    case 'REBALANCE_NEEDS_WORKING_BIKE':
      return 'переставлять можно только рабочий байк'
    // Самый частый штатный отказ на «Взять», и до этой правки единственный из
    // частых, что доезжал до скаута по-английски: сервер отвечает
    // «task already claimed or not open». Один и тот же reason приходит и
    // когда задачу перехватил другой, и когда её успели закрыть, — различить
    // их нечем, поэтому текст говорит про исход, а не про причину.
    case 'SCOUT_TASK_UNAVAILABLE':
      return 'задачу уже взял кто-то другой'
    // Пул водителя. NOT_FOUND — смену закрыли или байк сняли с пула.
    case 'POOL_ITEM_NOT_FOUND':
      return 'байка уже нет в вашем пуле'
    case 'POOL_ITEM_STATE':
      return 'байк в пуле уже в другом состоянии'
    case 'POOL_ITEM_PICKED':
      return 'байк уже в машине — отметьте «Поставил»'
    case 'POSITION_REQUIRED':
      return 'нужна ваша геопозиция'
    // Сборщик: бронь истекла и снята свипером или задание закрыто.
    case 'COLLECTOR_NOT_HOLDER':
      return 'это задание уже не ваше'
    case 'COLLECTOR_TASK_STATE':
      return 'задание уже в другом состоянии'
    case 'LOCK_FAILED':
      return 'замок не закрылся — попробуйте ещё раз или верните велосипед на HUB'
    case 'DEVICE_OFFLINE':
      return 'велосипед не на связи'
    case 'BIKE_LOCK_UNAVAILABLE':
      return 'сервис занят — попробуйте через минуту'
    default:
      return e.message
  }
}

let draining = false

/**
 * Досылает очередь по порядку.
 *
 * Строго последовательно: «взять» обязано уйти раньше «завершить» по той же
 * задаче, иначе сервер справедливо ответит, что задача не взята.
 * Повторный вызов во время работы — no-op.
 *
 * Возвращает записи, отклонённые сервером по существу: интерфейс обязан
 * показать их скауту, а не проглотить.
 */
export async function drainOutbox(): Promise<OutboxEntry[]> {
  if (draining) return []
  draining = true
  // Досылку зовут из эффектов и из подписки на сеть — фонового вызывающего,
  // который не станет ловить ошибку. Поэтому сбой хранилища гасится здесь:
  // необработанный промис в проде виден только как молчание.
  try {
    return await drain()
  } catch (e) {
    console.error('[outbox] досылка прервана', e)
    return []
  } finally {
    draining = false
  }
}

async function drain(): Promise<OutboxEntry[]> {
  const rejected: OutboxEntry[] = []
  const entries = await readOutbox()
  const keep: OutboxEntry[] = []

  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i]
    if (!entry) continue
    const result = await send(entry)
    if (result.outcome === 'sent') continue
    if (result.outcome === 'rejected') {
      // Причина едет вместе с записью: сама запись сейчас исчезнет из очереди,
      // и другого носителя у неё нет.
      // lastReason подставляется только когда он есть: под
      // exactOptionalPropertyTypes «поля нет» и «поле undefined» — разные вещи.
      rejected.push({
        ...entry,
        lastError: result.error,
        lastStatus: result.status,
        ...(result.reason === undefined ? {} : { lastReason: result.reason }),
      })
      continue
    }
    // retry: сохраняем эту и ВСЕ следующие, не нарушая порядок.
    keep.push({ ...entry, attempts: entry.attempts + 1 })
    for (let j = i + 1; j < entries.length; j += 1) {
      const rest = entries[j]
      if (rest) keep.push(rest)
    }
    break
  }

  await writeOutbox(keep)
  await notify()
  return rejected
}

/**
 * Человеческий текст отказа.
 *
 * Отказ приходит асинхронно, спустя минуты после нажатия, поэтому текст обязан
 * САМ называть, о каком действии речь: «не удалось» без подлежащего скауту
 * бесполезно — он с тех пор нажал ещё пять кнопок.
 *
 * Причина сервера приписывается, только если она известна: пустое «()» хуже,
 * чем его отсутствие.
 */
/**
 * Отказы, которые НЕ являются сбоем.
 *
 * «На этот байк задача уже есть» означает, что мир уже в том состоянии,
 * которого скаут добивался: кто-то опередил его на несколько секунд. Красный
 * тревожный тон здесь обучает не доверять предупреждениям вообще — а рядом
 * теми же средствами показывается «действие не сохранено», которое сбой
 * настоящий.
 */
const BENIGN_REASONS = new Set([
  'VEHICLE_HAS_ACTIVE_TASK',
  // «Задачу уже взял другой» — из того же разряда: скаут ничего не сломал,
  // просто опоздал на несколько секунд. Красный тост здесь приучает не читать
  // красные тосты вообще.
  'SCOUT_TASK_UNAVAILABLE',
  /*
   * «У вас уже есть начатая перестановка» — тоже не сбой, и приходит он в
   * самый неудачный момент.
   *
   * У блока «Перестановка» на карточке байка есть окно в один сетевой обмен:
   * запись уже ушла на сервер, а задача ещё не приехала в «мои», и кнопка на
   * секунду возвращается. Попавший в это окно второй тап отбивает BFF — лимит
   * партнёра он проверяет ДО обращения к fleet, поэтому приходит именно
   * PARTNER_TASK_ACTIVE, а не VEHICLE_HAS_ACTIVE_TASK. Красный «Не удалось» в
   * ту же секунду, когда на экране появляется «Завершить», сообщал бы о сбое,
   * которого не было.
   */
  'PARTNER_TASK_ACTIVE',
])

export function rejectedTone(entry: OutboxEntry): 'error' | 'info' {
  return entry.lastReason && BENIGN_REASONS.has(entry.lastReason) ? 'info' : 'error'
}

/**
 * Штатные исходы — без приставки «не удалось»: она противоречит смыслу.
 *
 * Текст на каждый reason свой. Общей формулировки тут быть не может: «задача
 * уже есть» и «задачу взял другой» — разные новости, и вторая требует от
 * скаута выбрать другую работу, а не смотреть на список.
 */
const BENIGN_TEXT: Record<string, string> = {
  VEHICLE_HAS_ACTIVE_TASK: 'На этот байк задача уже есть — её видит и диспетчер',
  SCOUT_TASK_UNAVAILABLE: 'Задачу уже взял кто-то другой — возьмите следующую',
  PARTNER_TASK_ACTIVE: 'У вас уже есть начатая перестановка — сначала завершите её',
}

/**
 * Что именно не удалось — по видам записи.
 *
 * ⚠ ТАБЛИЦА, А НЕ ЦЕПОЧКА ТЕРНАРНИКОВ, и это не вкусовщина. Цепочка
 * заканчивалась ветвью «Не удалось сменить статус байка», и новый вид записи,
 * забытый в ней, молча получал чужое имя. Здесь ключ — `OutboxKind` целиком,
 * поэтому забытый вид не собирается.
 */
const FAILED_TEXT: Record<OutboxKind, string> = {
  claim: 'Не удалось взять задачу',
  complete: 'Не удалось завершить задачу',
  create_pickup: 'Не удалось создать задачу «Забрать»',
  create_rebalance: 'Не удалось создать задачу «Переставить»',
  vehicle_report: 'Не удалось отправить замечание',
  vehicle_status: 'Не удалось сменить статус байка',
  label_attach: 'Не удалось добавить лейбл',
  label_detach: 'Не удалось снять лейбл',
  pool_pick: 'Не удалось отметить «Забрал»',
  pool_deliver: 'Не удалось отметить «Поставил»',
  pool_drop: 'Не удалось убрать из пула',
  collector_cancel: 'Не удалось отменить задание сборщика',
}

export function rejectedText(entry: OutboxEntry): string {
  if (rejectedTone(entry) === 'info') {
    return BENIGN_TEXT[entry.lastReason ?? ''] ?? 'Действие уже не нужно'
  }

  /*
   * Пока CreateScoutTask под RequireStaff (CLAUDE.md, разрыв 11), под ролью
   * scout сервер отвечает 403. Это не поломка приложения, и говорить об этом
   * надо словами про выкат, а не «недостаточно прав». Ветка исчезнет вместе
   * со строкой 11 таблицы.
   */
  if (entry.kind === 'create_pickup' && entry.lastReason === 'STAFF_REQUIRED') {
    return 'Сервер пока не разрешает скауту создавать задачи — ждём выката'
  }

  /*
   * Окно выката: клиент уже умеет «В ремонт» и «Вернуть в линию», а сервер
   * этих маршрутов ещё не знает и отвечает 404. Английский текст Kratos в
   * этом месте выглядит как поломка приложения, хотя приложение исправно.
   *
   * Ветка снимается вместе с выкатом `MarkServicing` и открытого скауту
   * `MarkRecovered` — до тех пор она честнее, чем «Not Found».
   */
  if (entry.kind === 'vehicle_status' && entry.lastStatus === 404) {
    return 'Сервер пока не умеет этот статус — ждём выката'
  }

  return entry.lastError ? `${FAILED_TEXT[entry.kind]}: ${entry.lastError}` : FAILED_TEXT[entry.kind]
}

/**
 * Цели уже поставленных в очередь действий такого вида.
 *
 * Нужна экранам, чтобы не предлагать во второй раз то, что ещё не уехало.
 * Без сети это единственная защита от двойного нажатия: сервер, который
 * ответил бы 409, недоступен ровно тогда, когда очередь и нужна.
 */
export function queuedTargets(entries: readonly OutboxEntry[], kind: OutboxKind): Set<string> {
  return new Set(entries.filter((e) => e.kind === kind).map((e) => e.targetId))
}

/** Убирает запись из очереди — после того, как скаут увидел отказ. */
export async function dropEntry(id: string): Promise<void> {
  const entries = await readOutbox()
  await writeOutbox(entries.filter((e) => e.id !== id))
  await notify()
}

/**
 * Полная очистка при выходе и при смене пользователя.
 *
 * Обязательна: в IndexedDB лежат задачи конкретного скаута. Если на общем
 * телефоне следующий увидит чужой список, это утечка — та самая, ради которой
 * Aida-PWA вообще запретила кэшировать /v1/*.
 */
export async function clearOutbox(): Promise<void> {
  // Не бросает: выход из системы обязан состояться в любом случае. Если
  // хранилище недоступно, то и стирать в нём нечего.
  try {
    await del(outboxKey())
  } catch (e) {
    console.error('[outbox] не удалось очистить очередь', e)
  }
  await notify()
}
